import { zodResolver } from '@hookform/resolvers/zod';
import { FileText, House, Link, Loader2, Mail, Phone, Plus, Signature, Tag, Trash2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { refreshCustomerConversation } from '@/actions/webchat';
import { BottomSheetTextButton } from '@/components/mobile';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select-form-field';
import {
  PHONE_DIALOG_BODY_CLASS,
  PHONE_DIALOG_CLASS,
  PHONE_DIALOG_HEADER_CLASS,
  PHONE_DIALOG_TITLE_CLASS,
} from '@/components/webchat/teams/phoneDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

import { useUpdateCustomerMutation } from '@/slices/customerSlice';
import { useUpdateProjectMutation } from '@/slices/projectSlice';
import { type CustomerData, customerSchema } from '@/types/validationSchemas/customerSchema'; // Updated import path
import formatPhoneForAPI from '@/utils/formatPhoneForAPI';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { createUniqueOptions } from '@/utils/multiSelectHelpers';

const getCaretPositionFromDigitIndex = (formattedValue: string, digitIndex: number) => {
  if (digitIndex <= 0) {
    return 0;
  }

  let digitsSeen = 0;
  for (let i = 0; i < formattedValue.length; i += 1) {
    if (/\d/.test(formattedValue[i])) {
      digitsSeen += 1;
      if (digitsSeen >= digitIndex) {
        return i + 1;
      }
    }
  }

  return formattedValue.length;
};

const fieldInputClass =
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive';

const FieldLabel = ({
  icon: Icon,
  htmlFor,
  children,
}: {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  htmlFor?: string;
  children: React.ReactNode;
}) => (
  <Label htmlFor={htmlFor} className="mb-1.5 flex items-center gap-1.5">
    <Icon className="size-3.5 text-muted-foreground" aria-hidden />
    {children}
  </Label>
);

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <p role="alert" className="mt-1.5 text-xs text-destructive">
      {message}
    </p>
  ) : null;

export function CustomerDialog({
  open,
  setOpen,
  customerInfo,
  createCustomerAndLinkToProject,
  _project,
  onSaved,
  title,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  customerInfo: (CustomerData & { _business?: string }) | null;
  createCustomerAndLinkToProject: (data: CustomerData) => void;
  _project: string | undefined;
  /** called with the final form data after a successful customer update */
  onSaved?: (data: CustomerData) => void;
  /** overrides the dialog title (e.g. when editing an unsaved compose chip) */
  title?: string;
}) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  // Phones submit from the Save button in the header, outside the form.
  const formId = useId();

  // Process customerInfo to replace null values with empty strings for specific fields
  // This is so zod doesn't throw an error when the field is null
  const processedCustomerInfo = useMemo(
    () => ({
      ...customerInfo,
      name: customerInfo?.name ?? '',
      phone: customerInfo?.phone ? formatPhoneForUser(customerInfo.phone) : '',
      email: customerInfo?.email ?? '',
      // Ensure other fields like tags, billingEmail, billingAddress, details are handled appropriately
      // If they can be null and the schema expects string, add similar handling
      tags: customerInfo?.tags ?? [], // Assuming tags should default to an empty array if null/undefined
      billingEmail: customerInfo?.billingEmail ?? '',
      billingAddress: customerInfo?.billingAddress ?? '',
      details: customerInfo?.details ?? '',
      associatedLinks: customerInfo?.associatedLinks ?? [],
    }),
    [customerInfo],
  );

  const { control, handleSubmit, reset } = useForm<CustomerData>({
    resolver: zodResolver(customerSchema),
    defaultValues: processedCustomerInfo, // Use processed values
  });

  const {
    fields: associatedLinksFields,
    append: appendAssociatedLink,
    remove: removeAssociatedLink,
  } = useFieldArray({
    control,
    name: 'associatedLinks',
  });

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus management: only focus the name field if it's empty, otherwise blur it
  useEffect(() => {
    if (open) {
      // Use a small delay to ensure the dialog is fully rendered and Radix has set focus
      setTimeout(() => {
        if (!processedCustomerInfo.name || processedCustomerInfo.name.trim() === '') {
          // Name is empty, ensure it's focused
          nameInputRef.current?.focus();
        } else {
          // Name has a value, remove focus from it if it's currently focused
          if (document.activeElement === nameInputRef.current) {
            nameInputRef.current?.blur();
          }
        }
      }, 50);
    }
  }, [open, processedCustomerInfo.name]);

  const [updateProject] = useUpdateProjectMutation();

  const tags = useSelector((state: { tags: string[] }) => state.tags);

  const [updateCustomer] = useUpdateCustomerMutation();

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Determine if we're in creation mode
  const isCreating = !customerInfo?._id || customerInfo._id === '';

  // Reset form and loading state when dialog closes or when customerInfo changes
  useEffect(() => {
    if (!open) {
      // Always reset loading state when dialog closes
      setIsLoading(false);

      // Reset to empty values when dialog closes in create mode
      if (isCreating) {
        reset({
          name: '',
          phone: '',
          email: '',
          details: '',
          tags: [],
          billingEmail: '',
          billingAddress: '',
          associatedLinks: [],
        });
      } else {
        // Reset to current customer info when editing
        reset(processedCustomerInfo);
      }
    } else {
      // When opening, reset to appropriate values
      reset(processedCustomerInfo);
    }
  }, [open, isCreating, reset, processedCustomerInfo]);

  const submit = async (data: CustomerData) => {
    try {
      setIsLoading(true);

      // Format phone for API if present
      const formattedData = {
        ...data,
        phone: data.phone ? formatPhoneForAPI(data.phone) : data.phone,
      };

      if (!customerInfo?._id || customerInfo._id === '') {
        // For creation, let the parent handle the API call
        await createCustomerAndLinkToProject(formattedData);
        // Reset form after successful creation
        reset({
          name: '',
          phone: '',
          email: '',
          details: '',
          tags: [],
          billingEmail: '',
          billingAddress: '',
          associatedLinks: [],
        });
        setIsLoading(false);
        setOpen(false);
        return;
      }

      await updateCustomer({
        ...formattedData,
        _customer: customerInfo._id,
        _business: customerInfo._business,
      }).unwrap();

      if (_project) {
        const projectData = {
          _id: _project,
          _customer: customerInfo._id,
        };

        // need to invalidate the project slice to get the latest customer
        await updateProject({ projectData }).unwrap();
      }

      // we need to update the customer in webchat if it is there
      dispatch(
        refreshCustomerConversation({
          _customer: customerInfo._id,
        }),
      );

      onSaved?.(formattedData);

      setIsLoading(false);
      setOpen(false);
    } catch (err: unknown) {
      // Always hide loading modal on error
      setIsLoading(false);

      let message = 'An unknown error occurred';

      // Handle RTK Query errors
      if (err && typeof err === 'object' && 'data' in err) {
        const errorData = err.data as {
          message?: string;
          error?: string | boolean;
        };
        if (errorData?.message) {
          message = errorData.message;
        } else if (errorData?.error && typeof errorData.error === 'string') {
          message = errorData.error;
        }
      }

      // Show appropriate error message based on operation
      const errorPrefix = isCreating ? 'Error creating customer' : 'Error updating customer';
      toast.error(`${errorPrefix}: ${message}`);
      console.error('Customer operation error:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(x) => {
        // Don't allow closing while loading
        if (!isLoading) {
          setOpen(x);
        }
      }}
    >
      <DialogContent
        mobileVariant="fullscreen"
        // Phones: an iOS navigation bar with Cancel and Save, so the fields get
        // the rest of the screen above the keyboard.
        hideClose="mobile"
        className={cn('max-h-[85vh] flex flex-col z-[60]', PHONE_DIALOG_CLASS)}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Loading overlay */}
        {isLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center space-y-4 p-8 bg-background rounded-lg shadow-lg border">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg font-medium text-center animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
                {isCreating ? 'Creating customer...' : 'Updating customer...'}
              </p>
              {isCreating && (
                <p className="text-sm text-muted-foreground text-center">
                  We&apos;ll take you to the conversation when ready
                </p>
              )}
            </div>
          </div>
        )}

        <DialogHeader className={PHONE_DIALOG_HEADER_CLASS}>
          {isMobile && (
            <BottomSheetTextButton
              className="justify-self-start"
              onClick={() => setOpen(false)}
              disabled={isLoading}
              data-testid="customer-cancel-button-mobile"
            >
              Cancel
            </BottomSheetTextButton>
          )}
          <DialogTitle className={PHONE_DIALOG_TITLE_CLASS}>
            {title ??
              (isCreating
                ? 'Create customer'
                : isMobile
                  ? 'Edit contact'
                  : 'Edit customer contact info')}
          </DialogTitle>
          <DialogDescription className="max-md:sr-only">
            Click save when you&apos;re done.
          </DialogDescription>
          {isMobile && (
            <BottomSheetTextButton
              strong
              type="submit"
              form={formId}
              className="justify-self-end"
              disabled={isLoading}
              data-testid="customer-save-button-mobile"
            >
              Save
            </BottomSheetTextButton>
          )}
        </DialogHeader>

        <div data-onboarding-guide-slot="contact" className="max-md:px-4 max-md:pt-3" />
        <form id={formId} data-onboarding={isCreating ? "contact-form" : undefined} onSubmit={handleSubmit(submit)} className="flex flex-col flex-1 min-h-0 md:max-xl:[@media(max-height:500px)]:flex-none">
          {/* Scrollable content area. On a landscape phone the whole dialog scrolls instead, so the fields keep their height. */}
          <div className={cn('overflow-y-auto flex-1 px-6 py-4 -mx-6 -mt-2 md:max-xl:[@media(max-height:500px)]:flex-none md:max-xl:[@media(max-height:500px)]:overflow-visible', PHONE_DIALOG_BODY_CLASS, 'max-md:m-0 max-md:px-4')}>
            <section aria-labelledby="customer-contact-heading" className="space-y-4">
              <h3 id="customer-contact-heading" className="text-base font-medium text-muted-foreground">
                Contact info
              </h3>

              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={Signature} htmlFor="customer-name">Name</FieldLabel>
                    <Input
                      {...field}
                      id="customer-name"
                      ref={nameInputRef}
                      autoComplete="off"
                      enterKeyHint="next"
                      aria-invalid={!!fieldState.error}
                      className={fieldInputClass}
                      data-testid="customer-name-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={Phone} htmlFor="customer-phone">Phone</FieldLabel>
                    <Input
                      {...field}
                      id="customer-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      enterKeyHint="next"
                      placeholder="123 456 7890"
                      aria-invalid={!!fieldState.error}
                      className={fieldInputClass}
                      onChange={(e) => {
                        const inputEl = e.target;
                        const rawValue = inputEl.value;
                        const selectionStart = inputEl.selectionStart ?? rawValue.length;
                        const digitIndex = rawValue
                          .slice(0, selectionStart)
                          .replace(/\D/g, '').length;
                        const formattedValue = formatPhoneForUser(rawValue);
                        field.onChange(formattedValue);
                        requestAnimationFrame(() => {
                          const newCaretPosition = getCaretPositionFromDigitIndex(
                            formattedValue,
                            digitIndex,
                          );
                          if (document.activeElement === inputEl) {
                            inputEl.setSelectionRange(newCaretPosition, newCaretPosition);
                          }
                        });
                      }}
                      value={field.value || ''}
                      data-testid="customer-phone-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={Mail} htmlFor="customer-email">Email</FieldLabel>
                    <Input
                      {...field}
                      id="customer-email"
                      type="email"
                      autoComplete="off"
                      autoCapitalize="none"
                      enterKeyHint="next"
                      placeholder="hello@business.com"
                      aria-invalid={!!fieldState.error}
                      className={fieldInputClass}
                      data-testid="customer-email-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />

              <Controller
                name="details"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={FileText} htmlFor="customer-details">Details</FieldLabel>
                    <Input
                      {...field}
                      id="customer-details"
                      placeholder="Anything you want to be able to search for"
                      aria-invalid={!!fieldState.error}
                      className={fieldInputClass}
                      data-testid="customer-details-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />

              <Controller
                name="tags"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={Tag}>Tags</FieldLabel>
                    <MultiSelect
                      {...field}
                      className="text-nowrap text-ellipsis"
                      options={createUniqueOptions(tags, field.value || [])}
                      defaultValue={field.value}
                      onValueChange={(newTags: string[]) => field.onChange(newTags)}
                      placeholder="Add a tag"
                      variant="secondary"
                      animation={0}
                      data-testid="customer-tags-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />

              <div>
                <FieldLabel icon={Link}>Associated links</FieldLabel>
                {associatedLinksFields.length > 0 && (
                  <div className="mb-2 space-y-2">
                    {associatedLinksFields.map((field, index) => (
                      <div key={field.id} className="flex items-start gap-2">
                        <Controller
                          name={`associatedLinks.${index}.name`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="min-w-0 flex-[2]">
                              <Input
                                {...field}
                                placeholder="Link name"
                                aria-label={`Link ${index + 1} name`}
                                aria-invalid={!!fieldState.error}
                                className={fieldInputClass}
                                data-testid={`customer-link-name-input-${index}`}
                              />
                              <FieldError message={fieldState.error?.message} />
                            </div>
                          )}
                        />
                        <Controller
                          name={`associatedLinks.${index}.url`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="min-w-0 flex-[3]">
                              <Input
                                {...field}
                                type="url"
                                placeholder="https://example.com"
                                aria-label={`Link ${index + 1} URL`}
                                aria-invalid={!!fieldState.error}
                                className={fieldInputClass}
                                data-testid={`customer-link-url-input-${index}`}
                              />
                              <FieldError message={fieldState.error?.message} />
                            </div>
                          )}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAssociatedLink(index)}
                          aria-label={`Remove link ${index + 1}`}
                          className="size-9 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          data-testid={`customer-remove-link-button-${index}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5 font-normal"
                  onClick={() =>
                    appendAssociatedLink({
                      name: '',
                      url: '',
                    })
                  }
                  disabled={associatedLinksFields.length >= 100}
                  data-testid="customer-add-link-button"
                >
                  <Plus className="size-3.5" />
                  Add link
                </Button>
              </div>
            </section>

            <section
              aria-labelledby="customer-billing-heading"
              className="mt-6 space-y-4 border-t border-border/60 pt-6"
            >
              <h3 id="customer-billing-heading" className="text-base font-medium text-muted-foreground">
                Billing info
              </h3>

              <Controller
                name="billingEmail"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={Mail} htmlFor="customer-billing-email">Billing email</FieldLabel>
                    <Input
                      {...field}
                      id="customer-billing-email"
                      type="email"
                      autoComplete="off"
                      autoCapitalize="none"
                      placeholder="invoice@business.com"
                      aria-invalid={!!fieldState.error}
                      className={fieldInputClass}
                      data-testid="customer-billing-email-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />

              <Controller
                name="billingAddress"
                control={control}
                render={({ field, fieldState }) => (
                  <div>
                    <FieldLabel icon={House} htmlFor="customer-billing-address">Billing address</FieldLabel>
                    <Input
                      {...field}
                      id="customer-billing-address"
                      autoComplete="off"
                      placeholder="123 Bank St."
                      aria-invalid={!!fieldState.error}
                      className={fieldInputClass}
                      data-testid="customer-billing-address-input"
                    />
                    <FieldError message={fieldState.error?.message} />
                  </div>
                )}
              />
            </section>
          </div>

          {/* Fixed footer with buttons (phones have them in the header) */}
          <DialogFooter className="mt-4 shrink-0 gap-2 border-t border-border/60 pt-4 sm:gap-0 max-md:hidden">
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
              }}
              disabled={isLoading}
              data-testid="customer-cancel-button"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-24"
              data-testid="customer-save-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isCreating ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
